import React, { useState, useEffect } from "react";
import { API } from "../lib/api";

/**
 * UserAvatar (Phase 10 Task A)
 * - Se `hasPhoto=true`, tenta di caricare da `/api/users/{userId}/photo`
 *   (blob URL come per document preview, per credentials cookie-auth robusto)
 * - Fallback: initials letter avatar con background deterministico dal userId
 */
const COLORS = [
    "#4A6FA5", "#B7854E", "#5A8A63", "#8B5A9C",
    "#C67B5C", "#4E828C", "#A86C6C", "#6B7A8F",
];

function initialsFrom(name) {
    if (!name) return "?";
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function bgFor(userId) {
    if (!userId) return COLORS[0];
    let h = 0;
    for (let i = 0; i < userId.length; i++) h = (h * 31 + userId.charCodeAt(i)) >>> 0;
    return COLORS[h % COLORS.length];
}

export default function UserAvatar({ userId, name, hasPhoto = true, size = 40, cacheBust }) {
    const [blobUrl, setBlobUrl] = useState(null);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        if (!hasPhoto || !userId) { setFailed(true); return; }
        let cancelled = false;
        let url = null;
        setFailed(false);
        setBlobUrl(null);
        (async () => {
            try {
                const res = await fetch(`${API}/users/${userId}/photo${cacheBust ? `?_=${cacheBust}` : ""}`, { credentials: "include" });
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const blob = await res.blob();
                if (cancelled) return;
                url = URL.createObjectURL(blob);
                setBlobUrl(url);
            } catch {
                if (!cancelled) setFailed(true);
            }
        })();
        return () => { cancelled = true; if (url) URL.revokeObjectURL(url); };
    }, [userId, hasPhoto, cacheBust]);

    const commonStyle = {
        width: size, height: size, borderRadius: "50%", flexShrink: 0,
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        overflow: "hidden", verticalAlign: "middle",
    };

    if (blobUrl && !failed) {
        return (
            <span style={commonStyle} data-testid={`user-avatar-${userId}`}>
                <img src={blobUrl} alt={name || "avatar"} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            </span>
        );
    }

    // Initials fallback
    return (
        <span
            style={{
                ...commonStyle,
                background: bgFor(userId),
                color: "#fff",
                fontFamily: "Archivo, ui-sans-serif, system-ui",
                fontWeight: 700,
                fontSize: Math.round(size * 0.4),
                letterSpacing: 0.3,
            }}
            aria-label={name || "avatar"}
            data-testid={`user-avatar-${userId}-initials`}
        >
            {initialsFrom(name)}
        </span>
    );
}
